import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import './MarketingSalesChart.css';

Chart.register(ChartDataLabels);

const BAR_COLORS = [
  '#3498db', '#9b59b6', '#1abc9c', '#f39c12', '#e74c3c',
  '#2ecc71', '#34495e', '#e67e22', '#16a085', '#8e44ad',
];

const VIEW_OPTIONS = [
  { id: 'marketing', label: 'Marketing-wise' },
  { id: 'product', label: 'Product-wise' },
  { id: 'delivery', label: 'Delivery mode' },
  { id: 'vehicle', label: 'Vehicle-wise' },
  { id: 'month', label: 'Month-wise' },
  { id: 'day', label: 'Day-wise' },
];

function parseSalesDate(value) {
  if (!value) return null;
  const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : null;
}

/** Indian FY starts in April (month index 3). */
export function getFinancialYearStartYear(refMoment) {
  const m = moment(refMoment);
  if (!m.isValid()) {
    const now = moment();
    return now.month() >= 3 ? now.year() : now.year() - 1;
  }
  return m.month() >= 3 ? m.year() : m.year() - 1;
}

export function getFinancialYearBounds(startYear) {
  const fyStart = moment({ year: startYear, month: 3, day: 1 }).startOf('day');
  const fyEnd = moment({ year: startYear + 1, month: 2, day: 1 }).endOf('month');
  const label = `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
  return { fyStart, fyEnd, label, startYear };
}

/**
 * Month-wise rows for one FY: Apr → Mar (include zero months).
 * Accepts either DC sales rows ({date, qty}) or monthly API rows ({month, qty}).
 */
export function aggregateMonthWiseByFinancialYear(salesOrMonthly, fyStartYear) {
  const { fyStart, fyEnd, label } = getFinancialYearBounds(fyStartYear);
  const months = [];
  for (let i = 0; i < 12; i += 1) {
    const monthMoment = fyStart.clone().add(i, 'months');
    months.push({
      key: monthMoment.format('YYYY-MM'),
      name: monthMoment.format('MMM'),
      qty: 0,
      sortKey: monthMoment.format('YYYY-MM'),
    });
  }

  const indexByKey = Object.fromEntries(months.map((row, index) => [row.key, index]));
  (salesOrMonthly || []).forEach((row) => {
    let key = null;
    let qty = Number(row.qty) || 0;
    if (row.month) {
      key = String(row.month).slice(0, 7);
    } else {
      const dt = parseSalesDate(row.date);
      if (!dt || dt.isBefore(fyStart, 'day') || dt.isAfter(fyEnd, 'day')) return;
      key = dt.format('YYYY-MM');
    }
    const index = indexByKey[key];
    if (index == null) return;
    months[index].qty = Number((months[index].qty + qty).toFixed(2));
  });

  return { rows: months, fyLabel: label, fyStart, fyEnd };
}

function toMoment(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value.clone() : null;
  const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : null;
}

/** Month bars for the dashboard period, using the same sales rows as the list. */
export function aggregateMonthsInRange(salesData, periodStart, periodEnd) {
  const rangeStart = toMoment(periodStart);
  const rangeEnd = toMoment(periodEnd);
  let start = rangeStart ? rangeStart.clone().startOf('month') : null;
  let end = rangeEnd ? rangeEnd.clone().startOf('month') : null;

  if (!start || !end) {
    (salesData || []).forEach((row) => {
      const dt = parseSalesDate(row.date);
      if (!dt) return;
      if (!start || dt.isBefore(start)) start = dt.clone().startOf('month');
      if (!end || dt.isAfter(end)) end = dt.clone().startOf('month');
    });
  }
  if (!start || !end) return { rows: [], periodLabel: '' };
  if (end.isBefore(start, 'month')) end = start.clone();

  const months = [];
  const cursor = start.clone();
  let guard = 0;
  while (cursor.isSameOrBefore(end, 'month') && guard < 36) {
    months.push({
      key: cursor.format('YYYY-MM'),
      name: start.year() === end.year() ? cursor.format('MMM') : cursor.format('MMM YY'),
      qty: 0,
      sortKey: cursor.format('YYYY-MM'),
    });
    cursor.add(1, 'month');
    guard += 1;
  }

  const indexByKey = Object.fromEntries(months.map((row, index) => [row.key, index]));
  const dayStart = (rangeStart || start).clone().startOf('day');
  const dayEnd = (rangeEnd || end.clone().endOf('month')).clone().endOf('day');
  let lastDataKey = null;

  (salesData || []).forEach((row) => {
    const dt = parseSalesDate(row.date);
    if (!dt || dt.isBefore(dayStart, 'day') || dt.isAfter(dayEnd, 'day')) return;
    const key = dt.format('YYYY-MM');
    const index = indexByKey[key];
    if (index == null) return;
    months[index].qty = Number((months[index].qty + (Number(row.qty) || 0)).toFixed(2));
    if (!lastDataKey || key > lastDataKey) lastDataKey = key;
  });

  const rows = lastDataKey ? months.filter((row) => row.sortKey <= lastDataKey) : months;
  const periodLabel = dayStart.isSame(dayEnd, 'day')
    ? dayStart.format('DD-MM-YYYY')
    : `${dayStart.format('DD-MM-YYYY')} to ${dayEnd.format('DD-MM-YYYY')}`;
  return { rows, periodLabel, fyLabel: periodLabel };
}

function bucketTopN(rows, topN = 8) {
  if (rows.length <= topN) return rows;
  const top = rows.slice(0, topN);
  const othersQty = rows.slice(topN).reduce((sum, row) => sum + row.qty, 0);
  if (othersQty > 0) {
    top.push({ name: 'Others', qty: Number(othersQty.toFixed(2)), sortKey: null });
  }
  return top;
}

function aggregateByKey(salesData, keyFn, { sortByQty = true, topN = null } = {}) {
  const totals = {};
  const meta = {};
  (salesData || []).forEach((row) => {
    const info = keyFn(row);
    if (!info) return;
    const { key, label, sortKey } = info;
    totals[key] = (totals[key] || 0) + (Number(row.qty) || 0);
    if (!meta[key]) {
      meta[key] = { label, sortKey };
    }
  });

  let rows = Object.entries(totals).map(([key, qty]) => ({
    name: meta[key].label,
    qty: Number(qty.toFixed(2)),
    sortKey: meta[key].sortKey,
  }));

  if (sortByQty) {
    rows.sort((a, b) => b.qty - a.qty);
  } else {
    rows.sort((a, b) => {
      if (a.sortKey == null && b.sortKey == null) return 0;
      if (a.sortKey == null) return 1;
      if (b.sortKey == null) return -1;
      if (a.sortKey < b.sortKey) return -1;
      if (a.sortKey > b.sortKey) return 1;
      return 0;
    });
  }

  if (topN) {
    rows = bucketTopN(rows, topN);
  }
  return rows;
}

export function aggregateSalesByView(salesData, view) {
  if (view === 'product') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.grade || '').trim() || 'Unspecified';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true, topN: 8 },
    );
  }

  if (view === 'delivery') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.delivery_mode || '').trim().toUpperCase() || 'UNKNOWN';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true },
    );
  }

  if (view === 'vehicle') {
    return aggregateByKey(
      salesData,
      (row) => {
        const name = String(row.vehiclenop || '').trim() || 'Unassigned';
        return { key: name, label: name, sortKey: name };
      },
      { sortByQty: true },
    );
  }

  if (view === 'day') {
    return aggregateByKey(
      salesData,
      (row) => {
        const dt = parseSalesDate(row.date);
        if (!dt) return null;
        return {
          key: dt.format('YYYY-MM-DD'),
          label: dt.format('DD MMM'),
          sortKey: dt.format('YYYY-MM-DD'),
        };
      },
      { sortByQty: false },
    );
  }

  // default: marketing-wise
  return aggregateByKey(
    salesData,
    (row) => {
      const name = String(row.marketing || '').trim() || 'Unassigned';
      return { key: name, label: name, sortKey: name };
    },
    { sortByQty: true, topN: 8 },
  );
}

function getViewMeta(view, periodLabel) {
  switch (view) {
    case 'product':
      return {
        title: 'Product-wise Sales Performance',
        subtitle: 'Delivered qty by grade (m³)',
      };
    case 'delivery':
      return {
        title: 'Delivery Mode Sales Performance',
        subtitle: 'Delivered qty by mode of delivery (m³)',
      };
    case 'vehicle':
      return {
        title: 'Vehicle-wise Sales Performance',
        subtitle: 'Delivered qty by vehicle (m³)',
      };
    case 'month':
      return {
        title: 'Month-wise Sales Performance',
        subtitle: periodLabel ? `${periodLabel} (m³)` : 'Delivered qty by month (m³)',
      };
    case 'day':
      return {
        title: 'Day-wise Sales Performance',
        subtitle: 'Delivered qty by day & month (m³)',
      };
    default:
      return {
        title: 'Marketing-wise Sales Performance',
        subtitle: 'Delivered qty by CSM (m³)',
      };
  }
}

function destroyChart(chartRef) {
  if (chartRef.current) {
    chartRef.current.destroy();
    chartRef.current = null;
  }
}

function monthTooltipTitle(view, rows, items) {
  if (view === 'month' && items[0]) {
    const key = rows[items[0].dataIndex]?.sortKey;
    return key ? moment(key, 'YYYY-MM').format('MMM YYYY') : items[0].label;
  }
  return items[0]?.label || '';
}

function renderBarChart(canvas, rows, { horizontal, isTimeAxis, view, monthAgg }) {
  return new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels: rows.map((row) => row.name),
      datasets: [{
        label: 'Sales Qty (m³)',
        data: rows.map((row) => row.qty),
        backgroundColor: rows.map((_, index) => BAR_COLORS[index % BAR_COLORS.length]),
        borderRadius: 4,
        maxBarThickness: isTimeAxis ? 36 : 28,
      }],
    },
    options: {
      indexAxis: horizontal ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => monthTooltipTitle(view, rows, items),
            label: (item) => `${Number(item.raw || 0).toFixed(2)} m³`,
          },
        },
        datalabels: {
          anchor: 'end',
          align: horizontal ? 'right' : 'top',
          color: '#2c3e50',
          font: { size: 10, weight: '600' },
          formatter: (value) => (Number(value) > 0 ? Number(value).toFixed(1) : ''),
          clamp: true,
        },
      },
      scales: horizontal
        ? {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                font: { size: 10 },
                callback: (value) => Number(value).toFixed(0),
              },
              title: { display: true, text: 'm³', font: { size: 11 } },
            },
            y: {
              grid: { display: false },
              ticks: { font: { size: 11 }, autoSkip: false },
            },
          }
        : {
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 10 },
                maxRotation: view === 'day' ? 45 : 0,
                minRotation: view === 'day' ? 45 : 0,
                autoSkip: view === 'day',
                maxTicksLimit: view === 'day' ? 16 : 12,
              },
              title: {
                display: true,
                text: view === 'day' ? 'Day & Month' : 'Month',
                font: { size: 11 },
              },
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                font: { size: 10 },
                precision: 0,
                maxTicksLimit: 6,
                callback: (value) => Number(value).toFixed(0),
              },
              title: { display: true, text: 'm³', font: { size: 11 } },
            },
          },
      layout: {
        padding: horizontal ? { right: 28 } : { top: 18, bottom: 4 },
      },
    },
    plugins: [ChartDataLabels],
  });
}

function renderPieChart(canvas, rows) {
  const slices = rows.filter((row) => row.qty > 0);
  const sliceTotal = slices.reduce((sum, row) => sum + row.qty, 0);
  return new Chart(canvas.getContext('2d'), {
    type: 'pie',
    data: {
      labels: slices.map((row) => row.name),
      datasets: [{
        data: slices.map((row) => row.qty),
        backgroundColor: slices.map((_, index) => BAR_COLORS[index % BAR_COLORS.length]),
        borderWidth: 2,
        borderColor: '#ffffff',
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 8,
            font: { size: 11 },
            color: '#2c3e50',
          },
        },
        tooltip: {
          callbacks: {
            label: (item) => {
              const qty = Number(item.raw || 0);
              const pct = sliceTotal > 0 ? (qty / sliceTotal) * 100 : 0;
              return ` ${qty.toFixed(2)} m³ (${pct.toFixed(1)}%)`;
            },
          },
        },
        datalabels: {
          color: '#ffffff',
          textStrokeColor: 'rgba(15, 23, 42, 0.45)',
          textStrokeWidth: 2,
          font: { size: 11, weight: '700' },
          formatter: (value) => {
            const pct = sliceTotal > 0 ? (Number(value) / sliceTotal) * 100 : 0;
            return pct >= 5 ? `${pct.toFixed(0)}%` : '';
          },
        },
      },
    },
    plugins: [ChartDataLabels],
  });
}

function MarketingSalesChart({
  salesData,
  periodStart,
  periodEnd,
}) {
  const [view, setView] = useState('marketing');
  const [chartOpen, setChartOpen] = useState(true);
  const canvasRef = useRef(null);
  const pieCanvasRef = useRef(null);
  const chartRef = useRef(null);
  const pieChartRef = useRef(null);

  const monthAgg = useMemo(() => {
    if (view !== 'month') return null;
    return aggregateMonthsInRange(salesData, periodStart, periodEnd);
  }, [view, salesData, periodStart, periodEnd]);

  const isTimeAxis = view === 'month' || view === 'day';
  const rows = useMemo(() => {
    if (view === 'month') return monthAgg?.rows || [];
    return aggregateSalesByView(salesData, view);
  }, [salesData, view, monthAgg]);

  const showPie = view !== 'month' && view !== 'day' && rows.some((row) => row.qty > 0);

  const totalQty = useMemo(
    () => rows.reduce((sum, row) => sum + row.qty, 0),
    [rows],
  );
  const meta = getViewMeta(view, monthAgg?.periodLabel);

  useEffect(() => {
    let cancelled = false;
    let frameId = 0;

    if (!chartOpen || !rows.length) {
      destroyChart(chartRef);
      destroyChart(pieChartRef);
      return undefined;
    }

    frameId = window.requestAnimationFrame(() => {
      if (cancelled || !canvasRef.current) return;

      const barExisting = Chart.getChart(canvasRef.current);
      if (barExisting) barExisting.destroy();
      chartRef.current = renderBarChart(canvasRef.current, rows, {
        horizontal: !isTimeAxis,
        isTimeAxis,
        view,
        monthAgg,
      });
      try {
        chartRef.current.resize();
      } catch (e) {
        /* canvas not laid out yet */
      }

      if (showPie && pieCanvasRef.current) {
        const pieExisting = Chart.getChart(pieCanvasRef.current);
        if (pieExisting) pieExisting.destroy();
        pieChartRef.current = renderPieChart(pieCanvasRef.current, rows);
        try {
          pieChartRef.current.resize();
        } catch (e) {
          /* canvas not laid out yet */
        }
      } else {
        destroyChart(pieChartRef);
      }
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      destroyChart(chartRef);
      destroyChart(pieChartRef);
    };
  }, [rows, showPie, isTimeAxis, view, monthAgg, chartOpen]);

  const canvasHeight = isTimeAxis
    ? 220
    : Math.max(220, Math.min(rows.length * 34 + 40, view === 'vehicle' ? 520 : 360));

  const emptyMessage = (() => {
    if (view === 'month' && totalQty <= 0) {
      return `No sales in ${monthAgg?.periodLabel || 'this period'}.`;
    }
    return 'No sales data for this period.';
  })();

  return (
    <div className={`marketing-sales-chart${chartOpen ? '' : ' is-collapsed'}`}>
      <div className="marketing-sales-chart-header">
        <div>
          <strong>{meta.title}</strong>
          <span className="marketing-sales-chart-sub">{meta.subtitle}</span>
        </div>
        <div className="marketing-sales-chart-header-actions">
          <div className="marketing-sales-chart-total">
            {totalQty.toFixed(2)} <small>m³</small>
          </div>
          <button
            type="button"
            className="marketing-sales-chart-toggle"
            onClick={() => setChartOpen((open) => !open)}
            aria-expanded={chartOpen}
          >
            {chartOpen ? 'Hide chart' : 'Show chart'}
          </button>
        </div>
      </div>

      {chartOpen ? (
        <>
          <div className="marketing-sales-chart-views" role="tablist" aria-label="Sales chart view">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={view === option.id}
                className={`marketing-sales-chart-view-btn ${view === option.id ? 'is-active' : ''}`}
                onClick={() => setView(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {!rows.length ? (
            <p className="marketing-sales-chart-empty">{emptyMessage}</p>
          ) : showPie ? (
            <div className="marketing-sales-chart-split" style={{ height: canvasHeight }}>
              <div className="marketing-sales-chart-pane">
                <canvas ref={canvasRef} />
              </div>
              <div className="marketing-sales-chart-pane">
                <canvas ref={pieCanvasRef} />
              </div>
            </div>
          ) : (
            <div className="marketing-sales-chart-pane" style={{ height: canvasHeight }}>
              <canvas ref={canvasRef} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default MarketingSalesChart;
